import { useProductMediaSummary } from "@/hooks/useProductMedia";

/**
 * A product row's media at a glance — an icon per FILLED slot, nothing for an
 * empty one (docs/plans/product-media.md §3). Tapping them opens the media
 * window: the short path for an admin who already knows they are there; the
 * row's "⋯ → Medios…" is the way in for a product with none yet.
 *
 * Dimming empty slots read almost the same as filled ones (the 📄 and 🎬 glyphs
 * are grey to begin with), so presence is the whole signal. The icons sit in a
 * FIXED-width box: the name input is `flex-1`, and an icon strip that grew and
 * shrank per row made every row's inputs a different width. `ml-auto` keeps
 * the box on the right when a narrow screen wraps the row.
 *
 * Every row reads the same cached summary: one request for the whole catalog.
 */
export function ProductMediaIndicator({
  productId,
  productName,
  onOpen,
}: {
  productId: string | undefined;
  productName: string;
  /** Omitted where the icons are only information (the inactive list). */
  onOpen?: () => void;
}): React.JSX.Element {
  const { data: summary } = useProductMediaSummary();
  const item = productId ? summary?.find((s) => s.productId === productId) : undefined;
  const galleryCount = item?.galleryCount ?? 0;
  const hasAny = galleryCount > 0 || item?.hasVideo === true || item?.hasPdf === true;

  return (
    <div className="ml-auto flex w-[88px] shrink-0 items-center justify-end self-center">
      {hasAny && (
        <IconsWrapper onOpen={onOpen} label={`Medios de ${productName}`}>
          {galleryCount > 0 && (
            <span
              className="text-[12px] tabular-nums text-ink-800"
              title={`Galería: ${galleryCount} fotos`}
            >
              🖼️ {galleryCount}
            </span>
          )}
          {item?.hasVideo && (
            <span className="text-[12px]" title="Tiene video">
              🎬
            </span>
          )}
          {item?.hasPdf && (
            <span className="text-[12px]" title="Tiene PDF">
              📄
            </span>
          )}
        </IconsWrapper>
      )}
    </div>
  );
}

function IconsWrapper({
  onOpen,
  label,
  children,
}: {
  onOpen: (() => void) | undefined;
  label: string;
  children: React.ReactNode;
}): React.JSX.Element {
  if (!onOpen) return <div className="flex items-center gap-2 px-1 py-0.5">{children}</div>;
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={label}
      className="flex items-center gap-2 rounded px-1 py-0.5 hover:bg-ink-050"
    >
      {children}
    </button>
  );
}
